import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const springSettleSkill: Skill = {
    name: SKILL_NAMES.springSettle,
    kind: 'codeo',
    description:
        'Give one element a physical arrival — overshoot and settle, with a squash on the way in ' +
        'and follow-through on what it lands next to — for a badge, a check, a toggle, or a ' +
        'confirmation that should feel tactile.',
    instructions: `# Spring Settle

An element arrives past its resting position and settles back into it, and something nearby reacts a beat later. Done well it reads as a physical object with mass; done badly it reads as a template's default bounce. The difference is entirely in restraint and in whether anything else responds.

Reach for it on confirmations and small objects — a check mark, a badge, a count landing, a toggle flipping. Never on a headline: type that bounces reads as unserious.

## Markup

\`\`\`html
<div id="confirm" style="position: absolute; left: 820px; top: 420px; width: 280px; height: 280px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: var(--l5-color-2, #38bdf8);">
  <svg id="confirm-mark" viewBox="0 0 100 100" style="width: 140px; height: 140px;">
    <path d="M24 52 L43 71 L78 32" fill="none" stroke="var(--l5-color-1, #101820)" stroke-width="11" stroke-linecap="round" stroke-linejoin="round" />
  </svg>
</div>
<div id="confirm-label" style="position: absolute; left: 0; right: 0; top: 750px; text-align: center; font-family: var(--l5-font-0), sans-serif; font-size: 54px; font-weight: 700; color: var(--l5-color-0, #101820);">Published</div>
\`\`\`

## Timeline code

\`back.out(N)\` is the overshoot. Keep \`N\` low — 1.4 is a firm settle, 3 is a cartoon.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const markPath = document.querySelector('#confirm-mark path');
const markLength = Math.ceil(markPath.getTotalLength()) + 2;
const LAND = 0.5;

gsap.set('#confirm', { scale: 0.4, opacity: 0 });
gsap.set(markPath, { strokeDasharray: markLength, strokeDashoffset: markLength + 1 });
gsap.set('#confirm-label', { y: 18, opacity: 0 });

tl.to('#confirm', { scale: 1, opacity: 1, duration: 0.62, ease: 'back.out(1.5)' }, LAND);
tl.to(markPath, { strokeDashoffset: 0, duration: 0.38, ease: 'power2.out' }, LAND + 0.22);
// Follow-through: the label reacts after the object lands, not with it.
tl.to('#confirm-label', { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' }, LAND + 0.34);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Overshoot amount**: \`back.out(1.2)\` is barely perceptible and suits a serious brand; \`back.out(1.8)\` is playful; past 2.2 it reads as a toy.
- **Squash**: tween \`scaleX\` and \`scaleY\` on slightly offset timings (0.03s apart) for a deformation on impact. Very small amounts — 1.06 against 0.94.
- **Elastic instead**: \`elastic.out(1, 0.5)\` oscillates rather than settling once. Reserve it for something genuinely springy; on anything else it reads as broken.
- **Follow-through distance**: the reacting element should move about a quarter of what the arriving one did, and start 0.08–0.14s later. That delay is the whole illusion.
- **Weight**: a heavier object overshoots less and settles longer. Scale \`back.out\`'s parameter down and the duration up together.
- **Chain**: two objects landing 0.12s apart, the second reacting to the first, reads as a sequence with causality rather than a stagger.

## Pitfalls

- The element is \`scale\`-tweened, so it must not carry a CSS \`transform\` — size and position it with \`left\`/\`top\`/\`width\`.
- Never spring a headline or a body line. Overshoot on type is the clearest "generated" tell in the library; use \`${SKILL_NAMES.headlineSlam}\` or \`${SKILL_NAMES.kineticWordReveal}\` for text.
- One springing object per graphic. Several bouncing at once reads as a loading screen.
- The follow-through has to be *later*. Simultaneous reaction is just a stagger and carries none of the physicality.
- Don't combine \`back.out\` with a rotation and a blur. The overshoot is the character; stacking effects buries it.
- \`getTotalLength()\` must be read from the live path after layout, which is why the dash setup is in the script rather than in the markup. Keep the \`+ 2\` on the dash and the \`+ 1\` on the offset, which park the whole path inside the pattern's gap. A dash equal to the measured length puts the path's ends on the dash/gap boundary, where sub-unit slop paints a fraction of a unit that round caps blow up into a full-width dot.
- \`gsap.set\` establishes every from-state at build time, so no tween needs \`immediateRender: false\`. See \`${SKILL_NAMES.motionPolish}\` for the wider overlap and easing vocabulary this belongs to.`,
};
